import { API_ERROR_DEDUPE_MS } from "./constants";
import { classifyApiError, isGlobalBoundaryError } from "./classifyApiError";

export const API_ERROR_EVENT = "nova-api-error";
export const API_ERROR_CLEAR_EVENT = "nova-api-error-clear";

let lastKey = "";
let lastAt = 0;

function dedupeKey(detail) {
  return [
    detail?.kind || "",
    detail?.status ?? "",
    detail?.code || "",
    detail?.url || "",
  ].join("|");
}

/**
 * Publish a classified API failure for the global boundary to consume.
 * Deduplicates identical bursts within API_ERROR_DEDUPE_MS.
 */
export function dispatchApiError(detail) {
  if (!detail || !isGlobalBoundaryError(detail)) {
    return false;
  }
  if (typeof window === "undefined") {
    return false;
  }

  const key = dedupeKey(detail);
  const now = Date.now();
  if (key === lastKey && now - lastAt < API_ERROR_DEDUPE_MS) {
    return false;
  }
  lastKey = key;
  lastAt = now;

  window.dispatchEvent(
    new CustomEvent(API_ERROR_EVENT, {
      detail: {
        ...detail,
        timestamp: detail.timestamp || new Date().toISOString(),
      },
    }),
  );
  return true;
}

/**
 * Classify an Axios/fetch failure and open the global support dialog when it
 * is a transport / service error (network, timeout, 408, 429, 5xx).
 * Skips cancelled requests, skipGlobalError, and business errors (4xx forms).
 */
export function publishTransportFailure(error, overrides = {}) {
  if (!error) return false;
  if (error.config?.skipGlobalError) return false;
  if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
    return false;
  }
  return dispatchApiError(classifyApiError(error, overrides));
}

/**
 * Attach a response interceptor that only publishes transport failures.
 * Use on clients that do not already handle 401/403 themselves.
 */
export function attachTransportErrorInterceptor(client, source) {
  if (!client?.interceptors?.response) return;
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      publishTransportFailure(error, { source });
      return Promise.reject(error);
    },
  );
}

/** Clear the global boundary panel (e.g. after Retry). */
export function clearApiError() {
  if (typeof window === "undefined") return;
  lastKey = "";
  lastAt = 0;
  window.dispatchEvent(new CustomEvent(API_ERROR_CLEAR_EVENT));
}

/**
 * Subscribe to global API error / clear events. Returns an unsubscribe fn.
 */
export function subscribeApiError({ onError, onClear } = {}) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const handleError = (event) => {
    if (onError) onError(event.detail);
  };
  const handleClear = () => {
    if (onClear) onClear();
  };

  window.addEventListener(API_ERROR_EVENT, handleError);
  window.addEventListener(API_ERROR_CLEAR_EVENT, handleClear);
  return () => {
    window.removeEventListener(API_ERROR_EVENT, handleError);
    window.removeEventListener(API_ERROR_CLEAR_EVENT, handleClear);
  };
}
